import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Activity,
  CreditCard,
  Repeat,
  Package,
  SlidersHorizontal,
  Network,
  Users,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import type { UserRole } from '@/types/auth';
import { cn } from '@/lib/utils';
import { Button, Separator, Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui';
import internetqLogo from '@/assets/internetq-logo.png';

interface NavItem {
  path: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  roles: UserRole[];
}

interface NavSection {
  labelKey: string;
  roles: UserRole[];
  items: NavItem[];
}

/**
 * Navigation sections with role-based access control.
 * Grouped by workflow context: Operations (daily work), Configuration (setup), Platform (infra).
 *
 * @see Spec 110 — Navigation Sektionen umgruppieren (5→3)
 * @see UI Critique Navigation 2026-03-12
 */
const navSections: NavSection[] = [
  {
    labelKey: 'nav.section.operations',
    roles: ['platform_admin', 'merchant_admin', 'merchant_viewer'],
    items: [
      {
        path: '/',
        labelKey: 'nav.dashboard',
        icon: LayoutDashboard,
        roles: ['platform_admin', 'merchant_admin', 'merchant_viewer'],
      },
      {
        path: '/transactions',
        labelKey: 'nav.transactions',
        icon: CreditCard,
        roles: ['platform_admin', 'merchant_admin'],
      },
      {
        path: '/subscriptions',
        labelKey: 'nav.subscriptions',
        icon: Repeat,
        roles: ['platform_admin', 'merchant_admin'],
      },
      {
        path: '/webhooks',
        labelKey: 'nav.webhookEvents',
        icon: Activity,
        roles: ['platform_admin', 'merchant_admin', 'merchant_viewer'],
      },
    ],
  },
  {
    labelKey: 'nav.section.configuration',
    roles: ['platform_admin', 'merchant_admin', 'merchant_viewer'],
    items: [
      {
        path: '/merchants',
        labelKey: 'nav.merchants',
        icon: Building2,
        roles: ['platform_admin', 'merchant_admin', 'merchant_viewer'],
      },
      {
        path: '/services',
        labelKey: 'nav.services',
        icon: Package,
        roles: ['platform_admin', 'merchant_admin'],
      },
      {
        path: '/webhooks/config',
        labelKey: 'nav.webhookConfig',
        icon: SlidersHorizontal,
        roles: ['platform_admin', 'merchant_admin'],
      },
    ],
  },
  {
    labelKey: 'nav.section.platform',
    roles: ['platform_admin'],
    items: [
      {
        path: '/iq-services',
        labelKey: 'nav.gatewayConfig',
        icon: Network,
        roles: ['platform_admin'],
      },
      {
        path: '/users',
        labelKey: 'nav.users',
        icon: Users,
        roles: ['platform_admin'],
      },
    ],
  },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

/**
 * Navigation sidebar component.
 * Responsive: Collapsible on desktop, hamburger menu on mobile.
 * Items grouped by domain with section headers.
 *
 * @see Spec 0012 Section 7.1-7.2 - App Shell Layout
 */
/**
 * Determines the `end` prop for NavLink active-state matching.
 * - Dashboard (/): end=true — otherwise it matches every route
 * - /webhooks/config: end=true — exact match to avoid collision with /webhooks
 * - All others: end=false — so detail pages like /transactions/txn_123 highlight the parent
 */
function getNavLinkEnd(path: string): boolean {
  if (path === '/' || path === '/webhooks/config') return true;
  return false;
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const { hasRole } = useAuth();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  /**
   * Custom active check for /webhooks: active when path starts with /webhooks
   * but NOT when it's /webhooks/config (that has its own NavLink).
   */
  const isWebhooksActive = (itemPath: string, isRouterActive: boolean): boolean => {
    if (itemPath === '/webhooks') {
      return location.pathname === '/webhooks' ||
        (location.pathname.startsWith('/webhooks/') && !location.pathname.startsWith('/webhooks/config'));
    }
    return isRouterActive;
  };

  const filteredSections = navSections
    .filter((section) => hasRole(section.roles))
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasRole(item.roles)),
    }))
    .filter((section) => section.items.length > 0);

  const navContent = (
    <nav className="flex flex-col gap-0.5 px-2 py-2" role="navigation" aria-label={t('aria.mainNavigation')}>
      {filteredSections.map((section, sectionIndex) => (
        <div key={section.labelKey}>
          {/* Section separator (not before first section) */}
          {sectionIndex > 0 && <Separator className="my-2" />}

          {/* Section header (hidden when collapsed) */}
          {!collapsed && (
            <span className="mb-1 flex items-center gap-1.5 px-3 pt-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <span className="inline-block h-2.5 w-0.5 rounded-full bg-brand-accent" aria-hidden="true" />
              {t(section.labelKey)}
            </span>
          )}

          {/* Section items */}
          {section.items.map((item) => {
            const Icon = item.icon;
            const label = t(item.labelKey);
            const navLink = (
              <NavLink
                key={item.path}
                to={item.path}
                end={getNavLinkEnd(item.path)}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }: { isActive: boolean }) =>
                  cn(
                    '!flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isWebhooksActive(item.path, isActive) ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                    collapsed && 'justify-center px-2'
                  )
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            );

            if (collapsed) {
              return (
                <TooltipProvider key={item.path} delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>{navLink}</TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }

            return navLink;
          })}
        </div>
      ))}
    </nav>
  );

  return (
    <>
      {/* Mobile toggle button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed left-4 top-[1.625rem] z-50 md:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-full border-r bg-card transition-all duration-300',
          'md:relative md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'w-14' : 'w-[17rem]'
        )}
        aria-label={t('aria.sidebar')}
      >
        {/* Brand accent line — synced with header stripe */}
        <div className="h-[3px] bg-brand-accent" />

        {/* Logo/Brand — synced with header via CSS var --header-total-height */}
        <div className="flex h-[var(--header-total-height)] items-center justify-center border-b px-3">
          {!collapsed && (
            <div className="flex flex-col items-center gap-1">
              <img
                src={internetqLogo}
                alt="InternetQ"
                className="h-9 w-auto dark:brightness-0 dark:invert"
              />
              <span className="text-[10px] font-medium text-muted-foreground">Admin Panel</span>
            </div>
          )}
          {collapsed && (
            <span className="text-sm font-bold text-brand-accent">iq</span>
          )}
        </div>

        {navContent}

        {/* Collapse toggle (desktop only) */}
        {onToggle && (
          <>
            <Separator className="my-2" />
            <div className="px-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                onClick={onToggle}
                aria-label={collapsed ? t('aria.expandSidebar') : t('aria.collapseSidebar')}
              >
                <Menu className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
